/**
 * QMS Standard Service (Phase 1)
 * Standards = processes; this service handles standard_requirements and evidence,
 * plus clone templates to organization (uses processService).
 */

import { db, storage } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import processService from './processService';
import { protocolService } from './protocolService';

const STANDARD_REQUIREMENTS = 'standard_requirements';
const EVIDENCE_ATTACHMENTS = 'evidence_attachments';

function removeUndefinedValues(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefinedValues);
  if (typeof obj !== 'object') return obj;
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) cleaned[key] = removeUndefinedValues(value);
  }
  return cleaned;
}

class QmsStandardService {
  constructor() {
    this.srRef = collection(db, STANDARD_REQUIREMENTS);
    this.evidenceRef = collection(db, EVIDENCE_ATTACHMENTS);
    this.protocolsRef = collection(db, 'protocols');
  }

  // ---------- Standard templates & clone (via processService) ----------

  async getStandardTemplates(filters = {}) {
    return processService.getStandardTemplates(filters);
  }

  async getStandard(standardId) {
    return processService.getProcess(standardId);
  }

  async getStandardsForOrganization(organizationId, filters = {}) {
    return processService.getProcesses(organizationId, filters);
  }

  /**
   * Clone all standard templates to an organization (create process instances + standard_requirements).
   */
  async cloneTemplatesToOrganization(organizationId, userId, userName) {
    const templates = await this.getStandardTemplates();
    return this._cloneTemplates(organizationId, templates, userId, userName);
  }

  /**
   * Clone only selected template IDs to an organization (e.g. by policy document).
   */
  async cloneSelectedTemplatesToOrganization(organizationId, templateIds, userId, userName) {
    if (!templateIds?.length) return [];
    const templates = await this.getStandardTemplates();
    const toClone = templates.filter((t) => templateIds.includes(t.id));
    return this._cloneTemplates(organizationId, toClone, userId, userName);
  }

  async _cloneTemplates(organizationId, templates, userId, userName) {
    const cloned = [];
    for (const t of templates) {
      const { id, createdAt, createdBy, updatedAt, updatedBy, ...rest } = t;
      const newProcess = await processService.createProcess(
        {
          ...rest,
          organizationId,
          isTemplate: false,
          clonedFromId: t.id,
          templateVersion: t.version || '1.0',
        },
        userId,
        userName
      );
      const existingLinks = await this.getStandardRequirementsByStandard(t.id);
      for (const sr of existingLinks) {
        await this.createStandardRequirement(
          {
            standardId: newProcess,
            requirementId: sr.requirementId,
            implementationStatus: 'Not Started',
          },
          userId,
          userName
        );
      }
      cloned.push({ templateId: t.id, newProcessId: newProcess });
    }
    return cloned;
  }

  /**
   * Clear all organization standards: archive all org processes and delete their standard_requirements.
   * Use before re-cloning templates. Requires userId/userName for audit.
   */
  async clearOrganizationStandards(organizationId, userId, userName) {
    const processes = await processService.getProcesses(organizationId);
    const processIds = processes.map((p) => p.id);
    if (processIds.length === 0) return { archived: 0, deletedSr: 0 };
    let deletedSr = 0;
    const chunkSize = 30;
    for (let i = 0; i < processIds.length; i += chunkSize) {
      const chunk = processIds.slice(i, i + chunkSize);
      const srSnap = await getDocs(query(this.srRef, where('standardId', 'in', chunk)));
      const batch = writeBatch(db);
      srSnap.docs.forEach((d) => batch.delete(d.ref));
      if (srSnap.docs.length > 0) await batch.commit();
      deletedSr += srSnap.size;
    }
    for (const process of processes) {
      await processService.deleteProcess(process.id, userId, userName, true);
    }
    return { archived: processes.length, deletedSr };
  }

  // ---------- Standard-Requirements junction ----------

  async getStandardRequirementsByStandard(standardId) {
    const q = query(
      this.srRef,
      where('standardId', '==', standardId),
      orderBy('implementationStatus', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async getStandardRequirementsByRequirement(requirementId) {
    const q = query(this.srRef, where('requirementId', '==', requirementId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /**
   * Get implemented-by standards for each requirement (for Policy Document Manager).
   * Returns Map<requirementId, Array<{ standardId, standardName }>>.
   */
  async getImplementedByForRequirements(requirementIds) {
    if (!requirementIds?.length) return {};
    const map = {};
    for (const rid of requirementIds) {
      map[rid] = [];
    }
    const links = [];
    for (const rid of requirementIds) {
      const srs = await this.getStandardRequirementsByRequirement(rid);
      const unique = new Set(srs.map((sr) => sr.standardId).filter(Boolean));
      unique.forEach((sid) => links.push({ requirementId: rid, standardId: sid }));
    }
    const standardIds = [...new Set(links.map((l) => l.standardId))];
    const nameMap = {};
    await Promise.all(
      standardIds.map(async (sid) => {
        try {
          const p = await processService.getProcess(sid);
          nameMap[sid] = p.processName || p.code || sid;
        } catch {
          nameMap[sid] = sid;
        }
      })
    );
    for (const link of links) {
      map[link.requirementId].push({
        standardId: link.standardId,
        standardName: nameMap[link.standardId] || link.standardId,
      });
    }
    return map;
  }

  /**
   * Get all standard_requirements for multiple standards (batch query).
   * Firestore 'in' limit is 30, so we batch in chunks.
   */
  async getStandardRequirementsForStandards(standardIds) {
    if (!standardIds?.length) return [];
    const results = [];
    const chunkSize = 30;
    for (let i = 0; i < standardIds.length; i += chunkSize) {
      const chunk = standardIds.slice(i, i + chunkSize);
      const q = query(this.srRef, where('standardId', 'in', chunk));
      const snapshot = await getDocs(q);
      results.push(...snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
    return results;
  }

  async getStandardRequirement(srId) {
    const ref = doc(this.srRef, srId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Standard-requirement link not found');
    return { id: snap.id, ...snap.data() };
  }

  async createStandardRequirement(data, userId, userName) {
    if (!data.standardId || !data.requirementId) {
      throw new Error('standardId and requirementId are required');
    }
    const now = Timestamp.now();
    const payload = removeUndefinedValues({
      standardId: data.standardId,
      requirementId: data.requirementId,
      implementationStatus: data.implementationStatus || 'Not Started',
      lastVerifiedAt: data.lastVerifiedAt ?? null,
      nextVerificationDue: data.nextVerificationDue ?? null,
      assignedOwnerId: data.assignedOwnerId ?? null,
      evidenceIds: data.evidenceIds || [],
      deviationThresholds: data.deviationThresholds ?? null,
      assignedProtocols: data.assignedProtocols || [],
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
    });
    const docRef = await addDoc(this.srRef, payload);
    return docRef.id;
  }

  async updateStandardRequirement(srId, updates, userId, userName) {
    const ref = doc(this.srRef, srId);
    const payload = removeUndefinedValues({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
    await updateDoc(ref, payload);
    return srId;
  }

  async deleteStandardRequirement(srId, userId, userName) {
    const ref = doc(this.srRef, srId);
    await deleteDoc(ref);
    return srId;
  }

  /**
   * Assign a protocol to a standard-requirement (PRD Protocol–Requirement linking).
   * Writes to both standard_requirement.assignedProtocols and protocol.linkedRequirements atomically.
   * @param {string} srId - standard_requirement document id
   * @param {{ protocolId: string, protocolName: string, protocolCode?: string, role: 'primary'|'supporting', requirementCode?: string, standardCode?: string }} params
   * @param {string} userId
   * @param {string} userName
   * @throws if requirement already has a primary and params.role is 'primary'
   */
  async assignProtocolToRequirement(srId, params, userId, userName) {
    const { protocolId, protocolName, protocolCode = '', role, requirementCode = '', standardCode = '' } = params;
    if (!srId || !protocolId || !protocolName || !role) {
      throw new Error('srId, protocolId, protocolName and role are required');
    }
    const link = await this.getStandardRequirement(srId);
    if (!link) throw new Error('Standard-requirement link not found');
    const requirementId = link.requirementId;
    const standardId = link.standardId;
    const assigned = link.assignedProtocols || [];
    const hasPrimary = assigned.some((a) => a.role === 'primary');
    if (role === 'primary' && hasPrimary) {
      throw new Error('A primary protocol is already assigned. Change the existing one to Supporting first, or assign this protocol as Supporting.');
    }
    const now = Timestamp.now();
    const newEntry = {
      protocolId,
      protocolName,
      protocolCode: protocolCode || (protocolName && protocolName.includes(' - ') ? protocolName.split(' - ')[0].trim() : ''),
      role: role === 'primary' ? 'primary' : 'supporting',
      assignedAt: now,
      assignedBy: userId,
    };
    const newAssignedProtocols = [...assigned, newEntry];

    const protocol = await protocolService.getProtocol(protocolId);
    const linkedReqs = (protocol?.linkedRequirements || []).filter(
      (r) => !(r.standardRequirementId === srId)
    );
    linkedReqs.push({
      requirementId,
      standardRequirementId: srId,
      requirementCode,
      standardCode,
      role: newEntry.role,
    });

    const batch = writeBatch(db);
    const srRef = doc(this.srRef, srId);
    const protocolRef = doc(this.protocolsRef, protocolId);
    batch.update(srRef, removeUndefinedValues({
      assignedProtocols: newAssignedProtocols,
      updatedAt: now,
      updatedBy: userId,
    }));
    batch.update(protocolRef, removeUndefinedValues({
      linkedRequirements: linkedReqs,
      updatedAt: now,
      updatedBy: userId,
    }));
    await batch.commit();
    return srId;
  }

  /**
   * Remove a protocol assignment from a standard-requirement. Updates both documents atomically.
   */
  async removeProtocolFromRequirement(srId, protocolId, userId, userName) {
    if (!srId || !protocolId) throw new Error('srId and protocolId are required');
    const link = await this.getStandardRequirement(srId);
    if (!link) throw new Error('Standard-requirement link not found');
    const assigned = (link.assignedProtocols || []).filter((a) => a.protocolId !== protocolId);

    const protocol = await protocolService.getProtocol(protocolId);
    const linkedReqs = (protocol?.linkedRequirements || []).filter(
      (r) => r.standardRequirementId !== srId
    );

    const now = Timestamp.now();
    const batch = writeBatch(db);
    const srRef = doc(this.srRef, srId);
    const protocolRef = doc(this.protocolsRef, protocolId);
    batch.update(srRef, removeUndefinedValues({
      assignedProtocols: assigned,
      updatedAt: now,
      updatedBy: userId,
    }));
    batch.update(protocolRef, removeUndefinedValues({
      linkedRequirements: linkedReqs,
      updatedAt: now,
      updatedBy: userId,
    }));
    await batch.commit();
    return srId;
  }

  // ---------- Evidence attachments ----------

  async listEvidence(standardRequirementId) {
    const q = query(
      this.evidenceRef,
      where('standardRequirementId', '==', standardRequirementId),
      orderBy('uploadedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async uploadEvidence(standardRequirementId, file, userId, userName, description = null) {
    const now = Timestamp.now();
    const newRef = doc(this.evidenceRef);
    const attId = newRef.id;
    const storagePath = `evidence/${standardRequirementId}/${attId}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    const payload = removeUndefinedValues({
      standardRequirementId,
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      storagePath,
      downloadURL,
      uploadedAt: now,
      uploadedBy: userId,
      uploadedByName: userName || userId,
      description: description || null,
    });
    await setDoc(newRef, payload);

    const sr = await this.getStandardRequirement(standardRequirementId);
    const evidenceIds = [...(sr.evidenceIds || []), attId];
    await this.updateStandardRequirement(standardRequirementId, { evidenceIds }, userId, userName);

    return attId;
  }

  async deleteEvidence(attId, userId, userName) {
    const evRef = doc(this.evidenceRef, attId);
    const snap = await getDoc(evRef);
    if (!snap.exists()) throw new Error('Evidence attachment not found');
    const data = snap.data();
    if (data.storagePath) {
      const storageRef = ref(storage, data.storagePath);
      await deleteObject(storageRef);
    }
    const srId = data.standardRequirementId;
    const sr = await this.getStandardRequirement(srId);
    const evidenceIds = (sr.evidenceIds || []).filter((id) => id !== attId);
    await this.updateStandardRequirement(srId, { evidenceIds }, userId, userName);
    await deleteDoc(evRef);
    return attId;
  }
}

export default new QmsStandardService();
